// API base URL - configurable via environment variable
// Set ADMIN_API_BASE_URL at build time to configure the API endpoint
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const process: {
  env: {
    ADMIN_API_BASE_URL?: string;
    ADMIN_DEV_AUTH_TOKEN?: string;
    ADMIN_DEV_AUTH_EMAIL?: string;
    ADMIN_DEV_AUTH_NAME?: string;
  };
};

const getApiBaseUrl = () => {
  // Use environment variable if set at build time (inlined by DefinePlugin)
  const envUrl = process.env.ADMIN_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }
  // Fallback to same origin
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();
const ADMIN_DEV_AUTH_TOKEN = process.env.ADMIN_DEV_AUTH_TOKEN?.trim() || '';
const ADMIN_DEV_AUTH_EMAIL =
  process.env.ADMIN_DEV_AUTH_EMAIL?.trim() || 'dev@cue.surf';
const ADMIN_DEV_AUTH_NAME =
  process.env.ADMIN_DEV_AUTH_NAME?.trim() || 'Dev Admin';

// Event emitter for auth-related events
type AuthEventListener = () => void;
const authEventListeners: Set<AuthEventListener> = new Set();

/**
 * Subscribe to auth failure events (401 responses)
 * Returns an unsubscribe function
 */
export function onAuthFailure(listener: AuthEventListener): () => void {
  authEventListeners.add(listener);
  return () => {
    authEventListeners.delete(listener);
  };
}

/**
 * Emit auth failure event to all listeners
 */
function emitAuthFailure(): void {
  authEventListeners.forEach(listener => listener());
}

/**
 * Get XSRF token from cookie
 * The server sets XSRF-TOKEN cookie, and we need to send it in X-XSRF-TOKEN header
 */
function getXSRFToken(): string | null {
  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length);
    }
  }
  return null;
}

/**
 * Add XSRF token header to request headers if available
 */
function addXSRFHeader(headers: Headers): Headers {
  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers.set('X-XSRF-TOKEN', xsrfToken);
  }
  return headers;
}

/**
 * Send a login verification code to the user's email
 * Returns the business ID for this verification request
 */
export async function sendLoginEmail(
  email: string
): Promise<{ bizId: string }> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/login?force_code=1`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, platform: 'admin' }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to send login email');
  }

  return response.json();
}

/**
 * Verify token and login
 * The JWT is set in an HTTP-only cookie by the server
 */
export async function verifyLoginToken(
  bizId: string,
  code: string
): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ bizId, code }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to verify token');
  }

  return response.json();
}

/**
 * Make an authenticated API request
 * Authentication is handled via HTTP-only cookie
 * XSRF protection is handled by reading XSRF-TOKEN cookie and sending it in X-XSRF-TOKEN header
 *
 * If the response is 401 Unauthorized, emits an auth failure event
 * so the auth context can redirect to login
 */
export async function authenticatedFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers);

  addXSRFHeader(headers);
  if (ADMIN_DEV_AUTH_TOKEN && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${ADMIN_DEV_AUTH_TOKEN}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include', // Important: send cookies with request
    headers,
  });

  // If unauthorized, emit auth failure event to trigger redirect to login
  if (response.status === 401) {
    emitAuthFailure();
  }

  return response;
}

/**
 * Logout user by invalidating session and removing JWT cookie
 */
export async function logout(): Promise<void> {
  const url = new URL('/v1/auth/logout', API_BASE_URL);

  const response = await fetch(url.toString(), {
    method: 'POST',
    credentials: 'include', // Important: send cookies to be cleared
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to logout');
  }
}

/**
 * Export getXSRFToken for debugging purposes
 */
export { getXSRFToken };

// Admin API types
export interface User {
  id: string;
  email: string;
  display_name?: string | null;
  avatar_url?: string | null;
  created_at: string;
  deleted_at?: string | null;
  account_status?: 'active' | 'deleted' | string;
  last_login_at?: string | null;
  onboarding_status?: string;
  onboarding_conversation_id?: string | null;
  active_offer_id?: string | null;
  onboarding_completed_at?: string | null;
  admin_note?: string | null;
  billing?: UserBillingSummary | null;
  invite?: UserInviteSummary | null;
}

export interface UserInviteSummary {
  code: string;
  source: string;
  benefit_type?: 'subscription_months' | 'one_time_plan' | string;
  benefit_plan_code?: string;
  benefit_plan_type?: 'addon' | 'bonus' | 'subscription' | string;
  benefit_duration_months?: number | null;
}

export interface UserBillingPlanSummary {
  plan_code: string;
  type: 'subscription' | 'addon' | string;
  grant_count: number;
  total_credits: number;
  used_credits: number;
  remaining_credits: number;
  first_granted_at?: string | null;
  last_granted_at?: string | null;
  next_expires_at?: string | null;
}

export interface UserBillingSummary {
  subscription_plans: UserBillingPlanSummary[];
  addon_plans: UserBillingPlanSummary[];
}

export function getDevAuthBootstrapUser(): User | null {
  if (!ADMIN_DEV_AUTH_TOKEN) {
    return null;
  }

  return {
    id: 'dev-admin',
    email: ADMIN_DEV_AUTH_EMAIL,
    display_name: ADMIN_DEV_AUTH_NAME,
    created_at: new Date(0).toISOString(),
  };
}

export interface AdminDevice {
  id: string;
  user_id: string;
  name: string;
  platform: string;
  app_version?: string;
  last_seen: string;
  created_at: string;
}

export interface ListUsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

export interface FetchUsersFilters {
  q?: string;
  onboardingStatus?: string;
  accountStatus?: string;
  subscriptionPlanCode?: string;
  inviteFilter?: string;
  inviteCode?: string;
}

export interface ListDevicesResponse {
  devices: AdminDevice[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserDevicesResponse {
  devices: AdminDevice[];
}

export interface BillingPlan {
  plan_code: string;
  type: string;
  credits_grant: number;
  period: string;
  currency: string;
  amount_cents: number;
  display_copy?: string | null;
}

export interface BillingPlansResponse {
  plans: BillingPlan[];
}

export interface BillingCredits {
  available_credits: number;
  next_reset_at?: string | null;
  plan_code?: string | null;
  sub_status?: string | null;
  sub_created_at?: string | null;
  sub_current_period_start?: string | null;
  sub_current_period_end?: string | null;
  sub_cancel_at?: string | null;
}

export interface ResetSubscriptionQuotaResponse {
  users: Array<{
    user_id: string;
    reset_credits: number;
    grant_count: number;
    available_credits: number;
  }>;
  total_reset_credits: number;
}

export interface GrantAdminAddonResponse {
  plan_code: string;
  users: Array<{
    user_id: string;
    credits_granted: number;
    already_granted: boolean;
    available_credits: number;
    credit_grant_id?: string;
    ledger_id?: number;
    expires_at?: string | null;
  }>;
  total_credits_granted: number;
}

export interface CheckoutSessionRequest {
  lookup_key: string;
  mode: 'subscription' | 'payment';
  success_url: string;
  cancel_url: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
}

export interface AdminAddonPlan {
  plan_code: string;
  credits_grant: number;
  period: string;
  currency: string;
  amount_cents: number;
  display_copy?: string | null;
  addon_source: string;
  created_at: string;
}

export interface ListAdminAddonsResponse {
  addons: AdminAddonPlan[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateAdminAddonInput {
  plan_code: string;
  credits_grant: number;
  currency: string;
  amount_cents: number;
  display_copy?: string | null;
}

export interface UpdateAdminAddonInput {
  credits_grant?: number;
  currency?: string;
  amount_cents?: number;
  display_copy?: string | null;
}

export interface AdminRedeemCode {
  id: string;
  code: string;
  plan_code: string;
  owner_user_id?: string | null;
  max_uses?: number | null;
  used_count: number;
  status: string;
  expires_at?: string | null;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface ListAdminRedeemCodesResponse {
  codes: AdminRedeemCode[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateRedeemCodeInput {
  code: string;
  plan_code: string;
  owner_user_id?: string | null;
  max_uses?: number | null;
  expires_at?: string | null;
}

export interface AdminInviteCode {
  id: string;
  code: string;
  owner_user_id?: string | null;
  max_uses?: number | null;
  used_count: number;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
  benefit?: AdminInviteCodeBenefit | null;
}

export interface AdminInviteCodeBenefit {
  id: string;
  type: 'subscription_months' | 'one_time_plan';
  plan_code: string;
  duration_months?: number | null;
  created_by_admin_user_id: string;
  created_at: string;
}

export interface ListAdminInviteCodesResponse {
  codes: AdminInviteCode[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateInviteCodeInput {
  code: string;
  owner_user_id?: string | null;
  max_uses?: number | null;
  benefit?: CreateInviteCodeBenefitInput | null;
}

export interface CreateInviteCodeBenefitInput {
  type: 'subscription_months' | 'one_time_plan';
  plan_code: string;
  duration_months?: number | null;
}

/**
 * Fetch all users (admin only)
 */
export async function fetchUsers(
  limit = 50,
  offset = 0,
  filters?: FetchUsersFilters
): Promise<ListUsersResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (filters?.q) {
    params.set('q', filters.q);
  }
  if (filters?.onboardingStatus) {
    params.set('onboarding_status', filters.onboardingStatus);
  }
  if (filters?.accountStatus) {
    params.set('account_status', filters.accountStatus);
  }
  if (filters?.subscriptionPlanCode) {
    params.set('subscription_plan_code', filters.subscriptionPlanCode);
  }
  if (filters?.inviteFilter) {
    params.set('invite_filter', filters.inviteFilter);
  }
  if (filters?.inviteCode) {
    params.set('invite_code', filters.inviteCode);
  }

  const response = await authenticatedFetch(
    `/v1/admin/users?${params.toString()}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch users');
  }

  return response.json();
}

/**
 * Extract a human-readable error message from an API error response.
 * Handles both structured JSON envelopes and plain-text bodies.
 */
export async function extractErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const text = await response.text();
  if (!text) return fallback;
  try {
    const json = JSON.parse(text);
    return json.error?.message || json.message || fallback;
  } catch {
    return text;
  }
}

/**
 * Fetch a single admin user with onboarding metadata.
 */
export async function fetchAdminUser(userId: string): Promise<User> {
  const response = await authenticatedFetch(`/v1/admin/users/${userId}`);

  if (!response.ok) {
    const message = await extractErrorMessage(response, 'Failed to fetch user');
    throw new Error(message);
  }

  return response.json();
}

/**
 * Approve a waitlist user into onboarding.
 */
export async function approveAdminUserOnboarding(
  userId: string
): Promise<User> {
  const response = await authenticatedFetch(
    `/v1/admin/users/${userId}/onboarding/approve`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      'Failed to approve onboarding'
    );
    throw new Error(message);
  }

  return response.json();
}

export async function resetSubscriptionQuota(
  userIds: string[]
): Promise<ResetSubscriptionQuotaResponse> {
  const response = await authenticatedFetch(
    '/v1/admin/billing/subscription-quota/reset',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_ids: userIds }),
    }
  );

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      'Failed to reset subscription quota'
    );
    throw new Error(message);
  }

  return response.json();
}

export async function grantAdminAddon(
  userIds: string[],
  planCode: string,
  input?: { idempotencyKey?: string; reason?: string }
): Promise<GrantAdminAddonResponse> {
  const response = await authenticatedFetch('/v1/admin/billing/addon-grants', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_ids: userIds,
      plan_code: planCode,
      idempotency_key: input?.idempotencyKey,
      reason: input?.reason,
    }),
  });

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      'Failed to grant addon'
    );
    throw new Error(message);
  }

  return response.json();
}

/**
 * Fetch all devices (admin only)
 */
export async function fetchDevices(
  limit = 50,
  offset = 0
): Promise<ListDevicesResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/devices?limit=${limit}&offset=${offset}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch devices');
  }

  return response.json();
}

/**
 * Fetch devices for a specific user (admin only)
 */
export async function fetchUserDevices(
  userId: string
): Promise<UserDevicesResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/users/${userId}/devices`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch user devices');
  }

  return response.json();
}

/**
 * Fetch available billing plans for the current user.
 */
export async function fetchBillingPlans(): Promise<BillingPlansResponse> {
  const response = await authenticatedFetch('/v1/plans');

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch billing plans');
  }

  return response.json();
}

/**
 * Fetch billing credits for the current user.
 */
export async function fetchBillingCredits(): Promise<BillingCredits> {
  const response = await authenticatedFetch('/v1/user/credits');

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch billing credits');
  }

  return response.json();
}

/**
 * Create a checkout session for a billing plan.
 */
export async function createCheckoutSession(
  input: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> {
  const response = await authenticatedFetch('/v1/user/billing/checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create checkout session');
  }

  return response.json();
}

export async function fetchAdminAddons(
  limit = 50,
  offset = 0
): Promise<ListAdminAddonsResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/addons?limit=${limit}&offset=${offset}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch addons');
  }

  return response.json();
}

export async function createAdminAddon(
  input: CreateAdminAddonInput
): Promise<AdminAddonPlan> {
  const response = await authenticatedFetch('/v1/admin/addons', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create addon');
  }

  return response.json();
}

export async function updateAdminAddon(
  planCode: string,
  input: UpdateAdminAddonInput
): Promise<AdminAddonPlan> {
  const response = await authenticatedFetch(`/v1/admin/addons/${planCode}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update addon');
  }

  return response.json();
}

export async function fetchAdminRedeemCodes(
  limit = 50,
  offset = 0,
  source = 'admin'
): Promise<ListAdminRedeemCodesResponse> {
  const params = new URLSearchParams({
    limit: `${limit}`,
    offset: `${offset}`,
    source,
  });
  const response = await authenticatedFetch(`/v1/admin/redeem-codes?${params}`);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch redeem codes');
  }

  return response.json();
}

export async function createAdminRedeemCode(
  input: CreateRedeemCodeInput
): Promise<AdminRedeemCode> {
  const response = await authenticatedFetch('/v1/admin/redeem-codes', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create redeem code');
  }

  return response.json();
}

export async function disableAdminRedeemCode(
  id: string
): Promise<AdminRedeemCode> {
  const response = await authenticatedFetch(
    `/v1/admin/redeem-codes/${id}/disable`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to disable redeem code');
  }

  return response.json();
}

export async function fetchAdminInviteCodes(
  limit = 50,
  offset = 0,
  source = 'admin'
): Promise<ListAdminInviteCodesResponse> {
  const params = new URLSearchParams({
    limit: `${limit}`,
    offset: `${offset}`,
    source,
  });
  const response = await authenticatedFetch(`/v1/admin/invite-codes?${params}`);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch invite codes');
  }

  return response.json();
}

export async function createAdminInviteCode(
  input: CreateInviteCodeInput
): Promise<AdminInviteCode> {
  const response = await authenticatedFetch('/v1/admin/invite-codes', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create invite code');
  }

  return response.json();
}

export async function disableAdminInviteCode(
  id: string
): Promise<AdminInviteCode> {
  const response = await authenticatedFetch(
    `/v1/admin/invite-codes/${id}/disable`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to disable invite code');
  }

  return response.json();
}

export interface EvalRevisionSet {
  evals_sha?: string;
  willow_sha?: string;
  cueboard_sha?: string;
  runner_sha?: string;
}

export interface EvalInputFingerprint {
  agent_prompt_hash?: string;
  env_description_hash?: string;
  task_bundle_hash?: string;
  suite_manifest_hash?: string;
}

export interface EvalArtifactRef {
  name: string;
  kind: string;
  uri: string;
}

export interface EvalTokenUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

export interface EvalToolCallRecord {
  call_id?: string;
  name: string;
  status?: string;
  timestamp?: string;
  duration_ms?: number;
  input?: string;
  output?: string;
}

export interface EvalSkillReadRecord {
  path: string;
  timestamp?: string;
}

export interface EvalTaskAggregate {
  task_ref: string;
  trial_count: number;
  passing_trial_count: number;
  average_trial_score?: number;
  aggregate_metrics?: Record<string, number>;
}

export interface EvalExperimentRecord {
  id: string;
  suite_ref?: string;
  suite_refs?: string[];
  revision_set?: EvalRevisionSet;
  input_fingerprint?: EvalInputFingerprint;
  aggregate_metrics?: Record<string, number>;
  task_aggregates?: EvalTaskAggregate[];
}

export interface EvalExperimentSummary {
  experiment_id?: string;
  task_count?: number;
  trial_count?: number;
  eval_mode?: string;
  supports_run?: boolean;
  cleanup_after_trial?: boolean;
  max_parallelism?: number;
  adapter?: string;
  environment_mode?: string;
  upload_route?: string;
  trial_upload_route?: string;
  transcript_strategy?: string;
}

export interface EvalGraderResult {
  grader_id: string;
  type?: string;
  status?: string;
  score?: number;
  pass?: boolean;
  reasoning?: string;
}

export interface EvalTrialRecord {
  id: string;
  task_ref?: string;
  attempt_index?: number;
  status?: string;
  environment_id?: string;
  agent_id?: string;
  session_id?: string;
  environment_metadata?: Record<string, string>;
  started_at?: string;
  completed_at?: string;
  transcript_ref?: string;
  artifact_refs?: EvalArtifactRef[];
  tool_calls?: EvalToolCallRecord[];
  skill_reads?: EvalSkillReadRecord[];
  usage?: EvalTokenUsage;
  summary?: string;
  checks?: string[];
  grader_results?: EvalGraderResult[];
}

export interface EvalRecordEnvelope<
  TRecord,
  TSummary = Record<string, unknown>,
> {
  received_at: string;
  idempotency_key: string;
  remote_addr?: string;
  record: TRecord;
  artifacts?: EvalArtifactRef[];
  summary?: TSummary;
}

export interface EvalFileViewResponse {
  path: string;
  content_type: string;
  size_bytes: number;
  content: string;
}

export interface EvalAnalyticsSummary {
  total_experiments: number;
  total_suites: number;
  total_adapters: number;
  total_eval_modes: number;
  total_trials: number;
  graded_trials: number;
  passing_trials: number;
  pass_rate?: number;
  average_score?: number;
  average_parallelism?: number;
  curve_averages?: Record<string, number>;
}

export interface EvalAnalyticsTrendPoint {
  bucket_start: string;
  experiment_count: number;
  trial_count: number;
  graded_trial_count: number;
  passing_trial_count: number;
  pass_rate?: number;
  average_score?: number;
  average_parallelism?: number;
  curve_averages?: Record<string, number>;
}

export interface EvalAnalyticsSuiteStat {
  suite_ref: string;
  label: string;
  experiment_count: number;
  trial_count: number;
  graded_trial_count: number;
  passing_trial_count: number;
  pass_rate?: number;
  average_score?: number;
  average_parallelism?: number;
  curve_averages?: Record<string, number>;
  last_received_at?: string;
}

export interface EvalAnalyticsAdapterStat {
  adapter: string;
  environment_modes?: string[];
  experiment_count: number;
  trial_count: number;
  graded_trial_count: number;
  passing_trial_count: number;
  pass_rate?: number;
  average_score?: number;
  average_parallelism?: number;
  curve_averages?: Record<string, number>;
  last_received_at?: string;
}

export interface EvalAnalyticsEvalModeStat {
  eval_mode: string;
  experiment_count: number;
  trial_count: number;
  graded_trial_count: number;
  passing_trial_count: number;
  pass_rate?: number;
  average_score?: number;
  average_parallelism?: number;
  curve_averages?: Record<string, number>;
  last_received_at?: string;
}

export interface EvalAnalyticsOverview {
  window: string;
  available_curve_keys: string[];
  summary: EvalAnalyticsSummary;
  trend: EvalAnalyticsTrendPoint[];
  suites: EvalAnalyticsSuiteStat[];
  adapters: EvalAnalyticsAdapterStat[];
  eval_modes: EvalAnalyticsEvalModeStat[];
}

export async function fetchEvalAnalyticsOverview(
  window: '7d' | '30d' | '90d' = '30d'
): Promise<EvalAnalyticsOverview> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/analytics/overview?window=${window}`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval analytics overview');
  }

  return response.json();
}

export async function fetchEvalExperiments(): Promise<
  EvalRecordEnvelope<EvalExperimentRecord, EvalExperimentSummary>[]
> {
  const response = await authenticatedFetch('/v1/admin/evals/experiments', {
    cache: 'no-store',
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval experiments');
  }

  const data = (await response.json()) as {
    experiments?: EvalRecordEnvelope<
      EvalExperimentRecord,
      EvalExperimentSummary
    >[];
  };
  return data.experiments || [];
}

export async function fetchEvalExperiment(
  experimentId: string
): Promise<EvalRecordEnvelope<EvalExperimentRecord, EvalExperimentSummary>> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval experiment');
  }

  return response.json();
}

export async function fetchEvalTrialIds(
  experimentId: string
): Promise<string[]> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval trials');
  }

  const data = (await response.json()) as { trials?: string[] };
  return (data.trials || []).map(trial =>
    trial.endsWith('.json') ? trial.slice(0, -'.json'.length) : trial
  );
}

export async function fetchEvalTrialEnvelopes(
  experimentId: string
): Promise<EvalRecordEnvelope<EvalTrialRecord>[]> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials/envelopes`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval trial envelopes');
  }

  const data = (await response.json()) as {
    trials?: EvalRecordEnvelope<EvalTrialRecord>[];
  };
  return data.trials || [];
}

export async function fetchEvalTrial(
  experimentId: string,
  trialId: string
): Promise<EvalRecordEnvelope<EvalTrialRecord>> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials/${encodeURIComponent(trialId)}`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval trial');
  }

  return response.json();
}

export async function fetchEvalExperimentArtifactView(
  experimentId: string,
  artifactName: string
): Promise<EvalFileViewResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/artifacts/${encodeURIComponent(artifactName)}/view`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval experiment artifact');
  }

  return response.json();
}

export async function fetchEvalTrialTranscriptView(
  experimentId: string,
  trialId: string
): Promise<EvalFileViewResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials/${encodeURIComponent(trialId)}/transcript/view`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval transcript');
  }

  return response.json();
}

export async function fetchEvalTrialArtifactView(
  experimentId: string,
  trialId: string,
  artifactName: string
): Promise<EvalFileViewResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials/${encodeURIComponent(trialId)}/artifacts/${encodeURIComponent(artifactName)}/view`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval trial artifact');
  }

  return response.json();
}

export async function fetchEvalTrialArtifactRaw(
  experimentId: string,
  trialId: string,
  artifactName: string
): Promise<Blob> {
  const response = await authenticatedFetch(
    `/v1/admin/evals/experiments/${encodeURIComponent(experimentId)}/trials/${encodeURIComponent(trialId)}/artifacts/${encodeURIComponent(artifactName)}/raw`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch eval trial artifact');
  }

  return response.blob();
}

export function isImageArtifact(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(
    ext
  );
}

export function isVideoArtifact(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext);
}

export function isTextArtifact(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return [
    'txt',
    'md',
    'json',
    'yaml',
    'yml',
    'xml',
    'csv',
    'log',
    'jsonl',
    'ndjson',
    'sh',
    'py',
    'go',
    'ts',
    'js',
    'html',
    'css',
  ].includes(ext);
}

// ==================== Skill API ====================

export interface SkillAuthor {
  id: string;
  display_name?: string;
  email: string;
  avatar_url?: string;
}

export interface Skill {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
  version: string;
  status: string;
  featured: boolean;
  hero_path?: string;
  hero_url?: string;
  video_path?: string;
  video_url?: string;
  markdown_content?: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  author_id?: string;
  author?: SkillAuthor;
  assets?: Record<string, string>; // asset filename -> signed URL mapping
  source?: string; // 'official' | 'community'
  submitter_id?: string;
  submitter?: SkillAuthor;
  review_status?: string; // 'pending_review' | 'approved' | 'rejected'
  review_note?: string;
  reviewed_at?: string;
  submitted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SkillCreate {
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
  version?: string;
  status?: string;
  featured?: boolean;
  hero_path?: string;
  video_path?: string;
  markdown_content?: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  author_id?: string;
}

export interface SkillUpdate {
  name?: string;
  display_name?: string;
  description?: string;
  category?: string;
  version?: string;
  status?: string;
  featured?: boolean;
  hero_path?: string;
  video_path?: string;
  markdown_content?: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  author_id?: string;
}

export interface ListSkillsResponse {
  skills: Skill[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Fetch skills (admin only)
 */
export async function fetchSkills(
  limit = 50,
  offset = 0,
  filters?: {
    status?: string;
    category?: string;
    featured?: boolean;
  }
): Promise<ListSkillsResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (filters?.status) params.append('status', filters.status);
  if (filters?.category) params.append('category', filters.category);
  if (filters?.featured !== undefined)
    params.append('featured', filters.featured.toString());

  const response = await authenticatedFetch(
    `/v1/admin/skills?${params.toString()}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch skills');
  }

  return response.json();
}

/**
 * Fetch a single skill (admin only)
 */
export async function fetchSkill(id: string): Promise<Skill> {
  const response = await authenticatedFetch(`/v1/admin/skills/${id}`);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch skill');
  }

  return response.json();
}

/**
 * Create a skill (admin only)
 */
export async function createSkill(data: SkillCreate): Promise<Skill> {
  const response = await authenticatedFetch('/v1/admin/skills', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create skill');
  }

  return response.json();
}

/**
 * Import a skill from a GitHub repository URL (admin only)
 * Supports URLs like: https://github.com/owner/repo/tree/branch/path/to/skill
 * @param url - The GitHub URL to the skill directory
 * @param token - Optional GitHub personal access token for higher rate limits
 */
export async function importSkillFromGitHub(
  url: string,
  token?: string
): Promise<Skill> {
  const response = await authenticatedFetch('/v1/admin/skills/import-github', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, token }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to import skill from GitHub');
  }

  return response.json();
}

/**
 * Update a skill (admin only)
 */
export async function updateSkill(
  id: string,
  data: SkillUpdate
): Promise<Skill> {
  const response = await authenticatedFetch(`/v1/admin/skills/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update skill');
  }

  return response.json();
}

/**
 * Delete a skill (admin only)
 */
export async function deleteSkill(id: string): Promise<void> {
  const response = await authenticatedFetch(`/v1/admin/skills/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to delete skill');
  }
}

/**
 * Sync skill metadata from SKILL.md frontmatter (admin only)
 */
export async function syncSkillMetadata(id: string): Promise<Skill> {
  const response = await authenticatedFetch(
    `/v1/admin/skills/${id}/sync-metadata`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const text = await response.text();
    let message = 'Failed to sync metadata';
    try {
      const json = JSON.parse(text);
      message = json.error?.message || message;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }

  return response.json();
}

export interface UploadedFileUrl {
  path: string;
  url: string;
}

/**
 * Upload skill files (admin only)
 */
export async function uploadSkillFiles(
  id: string,
  files: File[],
  dir: 'skill' | 'assets' = 'skill'
): Promise<{
  status: string;
  files_count: number;
  uploaded_files: string[];
  uploaded_file_urls?: UploadedFileUrl[];
  dir: string;
  validation_errors?: string[];
}> {
  const formData = new FormData();
  const filePaths: string[] = [];

  files.forEach(file => {
    formData.append('files', file);
    // Pass target paths separately since browsers strip path separators from filenames
    filePaths.push(file.name);
  });

  // Include paths as JSON to preserve path prefixes like "scripts/" or "resources/"
  formData.append('paths', JSON.stringify(filePaths));

  const headers = new Headers();
  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers.set('X-XSRF-TOKEN', xsrfToken);
  }

  const response = await fetch(
    `${API_BASE_URL}/v1/admin/skills/${id}/upload?dir=${dir}`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to upload skill files');
  }

  return response.json();
}

// Skill file types
export interface SkillFileInfo {
  path: string;
  name: string;
  size: number;
  last_modified: number;
  is_directory: boolean;
}

export interface ListSkillFilesResponse {
  files: SkillFileInfo[];
}

/**
 * List files for a skill (admin only)
 */
export async function fetchSkillFiles(
  id: string,
  dir: 'skill' | 'assets' = 'skill'
): Promise<ListSkillFilesResponse & { dir: string }> {
  const response = await authenticatedFetch(
    `/v1/admin/skills/${id}/files?dir=${dir}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch skill files');
  }

  return response.json();
}

/**
 * Get content of a skill file (admin only)
 */
export async function fetchSkillFileContent(
  id: string,
  filePath: string,
  dir: 'skill' | 'assets' = 'skill'
): Promise<{ content: string; contentType: string; blobUrl?: string }> {
  const response = await authenticatedFetch(
    `/v1/admin/skills/${id}/files/${filePath}?dir=${dir}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch file content');
  }

  const contentType = response.headers.get('content-type') || 'text/plain';

  // For images and other binary files, create a blob URL
  if (
    contentType.startsWith('image/') ||
    contentType.startsWith('video/') ||
    contentType.startsWith('audio/')
  ) {
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return { content: '', contentType, blobUrl };
  }

  // For text files, return the content
  const content = await response.text();
  return { content, contentType };
}

/**
 * Save skill file content (admin only)
 */
export async function saveSkillFileContent(
  id: string,
  filePath: string,
  content: string,
  dir: 'skill' | 'assets' = 'skill'
): Promise<{
  status: string;
  path: string;
  metadata_synced: boolean;
  sync_error?: string;
}> {
  const response = await authenticatedFetch(
    `/v1/admin/skills/${id}/files/${filePath}?dir=${dir}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'text/plain' },
      body: content,
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to save file');
  }

  return response.json();
}

/**
 * Delete a skill file (admin only)
 */
export async function deleteSkillFile(
  id: string,
  filePath: string,
  dir: 'skill' | 'assets' = 'skill'
): Promise<void> {
  const response = await authenticatedFetch(
    `/v1/admin/skills/${id}/files/${filePath}?dir=${dir}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to delete file');
  }
}

/**
 * Upload media (hero image or video) for a skill (admin only)
 */
export async function uploadSkillMedia(
  id: string,
  file: File,
  type: 'hero' | 'video'
): Promise<{ status: string; path: string; url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const headers = new Headers();
  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers.set('X-XSRF-TOKEN', xsrfToken);
  }

  const response = await fetch(
    `${API_BASE_URL}/v1/admin/skills/${id}/media?type=${type}`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `Failed to upload ${type}`);
  }

  return response.json();
}

// ============================================================================
// Blog API
// ============================================================================

export interface BlogAuthor {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
}

export interface Blog {
  id: string;
  title: string;
  slug: string;
  content?: string;
  cover_image?: string;
  cover_image_url?: string;
  status: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  author_id?: string;
  author?: BlogAuthor;
  skill_ids?: string[];
  assets?: Record<string, string>; // asset key -> signed URL mapping
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BlogCreate {
  title: string;
  slug?: string;
  content?: string;
  cover_image?: string;
  status?: string;
  author_id?: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  skill_ids?: string[];
}

export interface BlogUpdate {
  title?: string;
  slug?: string;
  content?: string;
  cover_image?: string;
  status?: string;
  author_id?: string;
  seo_title?: string;
  seo_description?: string;
  seo_image?: string;
  seo_keywords?: string[];
  canonical_url?: string;
  skill_ids?: string[];
}

export interface ListBlogsResponse {
  blogs: Blog[];
  total: number;
}

/**
 * Fetch blogs list (admin only)
 */
export async function fetchBlogs(
  limit = 20,
  offset = 0
): Promise<ListBlogsResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/blogs?limit=${limit}&offset=${offset}`
  );
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch blogs');
  }
  return response.json();
}

/**
 * Fetch a single blog by ID (admin only)
 */
export async function fetchBlog(id: string): Promise<Blog> {
  const response = await authenticatedFetch(`/v1/admin/blogs/${id}`);
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch blog');
  }
  return response.json();
}

/**
 * Create a new blog (admin only)
 */
export async function createBlog(data: BlogCreate): Promise<Blog> {
  const response = await authenticatedFetch('/v1/admin/blogs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create blog');
  }
  return response.json();
}

/**
 * Update a blog (admin only)
 */
export async function updateBlog(id: string, data: BlogUpdate): Promise<Blog> {
  const response = await authenticatedFetch(`/v1/admin/blogs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update blog');
  }
  return response.json();
}

/**
 * Delete a blog (admin only)
 */
export async function deleteBlog(id: string): Promise<void> {
  const response = await authenticatedFetch(`/v1/admin/blogs/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to delete blog');
  }
}

/**
 * Upload blog cover image (admin only)
 */
export async function uploadBlogCover(
  id: string,
  file: File
): Promise<{ path: string; url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const headers = new Headers();
  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers.set('X-XSRF-TOKEN', xsrfToken);
  }

  const response = await fetch(`${API_BASE_URL}/v1/admin/blogs/${id}/cover`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to upload cover');
  }

  return response.json();
}

/**
 * Upload blog asset (image/video for content) (admin only)
 * Returns the asset key to be stored in content (not a signed URL)
 */
export async function uploadBlogAsset(
  id: string,
  file: File
): Promise<{ key: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const headers = new Headers();
  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers.set('X-XSRF-TOKEN', xsrfToken);
  }

  const response = await fetch(`${API_BASE_URL}/v1/admin/blogs/${id}/assets`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to upload asset');
  }

  return response.json();
}

export interface HostThreadTopEntry {
  thread_id: number;
  name?: string;
  cpu_percent: number;
  user_time_usec: number;
  system_time_usec: number;
  run_state: number;
}

export interface HostThreadTopSnapshot {
  timestamp: string;
  threads: HostThreadTopEntry[];
}

export interface ProcessTopEntry {
  pid: number;
  comm: string;
  cpu_percent: number;
  delta_ticks: number;
}

export interface ProcessTopSnapshot {
  timestamp: string;
  processes: ProcessTopEntry[];
}

export type TelemetryArtifactParsed =
  | {
      kind: 'host_thread_topn';
      data: { snapshots: HostThreadTopSnapshot[] };
      message?: string;
    }
  | {
      kind: 'guest_process_topn';
      data: { snapshots: ProcessTopSnapshot[] };
      message?: string;
    }
  | {
      kind: 'flamegraph_svg';
      data: { svg: string; caption?: string };
      message?: string;
    }
  | {
      kind: 'text';
      data: { snippet: string; truncated?: boolean };
      message?: string;
    }
  | {
      kind: 'unknown';
      message?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data?: any;
    };

export interface TelemetryArtifactViewResponse {
  id: string;
  content_type?: string;
  size_bytes?: number;
  download_url?: string;
  artifact: TelemetryArtifactParsed;
}

export async function fetchTelemetryArtifact(
  id: string
): Promise<TelemetryArtifactViewResponse> {
  const response = await authenticatedFetch(
    `/v1/admin/telemetry/artifacts/${encodeURIComponent(id)}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch telemetry artifact');
  }

  return response.json();
}

/**
 * Get blog assets mapping (key -> signed URL) (admin only)
 */
export async function getBlogAssets(
  id: string
): Promise<Record<string, string>> {
  const response = await authenticatedFetch(`/v1/admin/blogs/${id}/assets`);
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to get blog assets');
  }
  const data = await response.json();
  return data.assets || {};
}

// ==================== Skill Comments ====================

export interface SkillComment {
  id: string;
  skill_name: string;
  user_display_name: string;
  score: number;
  comment: string;
  source: 'official' | 'external';
  repo?: string;
  created_at: string;
}

/**
 * Fetch skill comments with pagination and optional skill name filter (admin only)
 */
export async function fetchSkillComments(
  limit: number,
  offset: number,
  skillName?: string
): Promise<{ comments: SkillComment[]; total: number }> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });
  if (skillName) {
    params.set('skill_name', skillName);
  }
  const response = await authenticatedFetch(
    `/v1/admin/skill-comments?${params.toString()}`
  );
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch skill comments');
  }
  return response.json();
}

/**
 * Delete a skill comment by ID (admin only)
 */
export async function deleteSkillComment(id: string): Promise<void> {
  const response = await authenticatedFetch(`/v1/admin/skill-comments/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to delete skill comment');
  }
}

// ==================== Skill Review Queue ====================

/**
 * Fetch pending review skills (admin only)
 */
export async function fetchReviewQueue(
  limit = 50,
  offset = 0
): Promise<ListSkillsResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  const response = await authenticatedFetch(
    `/v1/admin/skills/review-queue?${params.toString()}`
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch review queue');
  }

  return response.json();
}

/**
 * Approve or reject a community skill (admin only)
 */
export async function reviewSkill(
  id: string,
  decision: { status: 'approved' | 'rejected'; note?: string }
): Promise<Skill> {
  const response = await authenticatedFetch(`/v1/admin/skills/${id}/review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(decision),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to review skill');
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Gallery (Book) admin API
// Backend routes:
//   POST   /v1/admin/gallery/submissions/:id/hide       {reason}
//   POST   /v1/admin/gallery/submissions/:id/restore    {reason}
//   DELETE /v1/admin/gallery/submissions/:id
// We also reuse the public feed (`GET /v1/public/gallery`) so the admin can
// see currently-published items, since the backend has no "list-all" admin
// endpoint yet.
// ---------------------------------------------------------------------------

export interface GallerySubmission {
  id: string;
  source_ref: {
    type?: string;
    agent_id?: string;
    site_name?: string;
    owner_id?: string;
    source_url?: string;
  };
  source_url: string;
  title: string;
  caption: string;
  creator_display_name: string;
  creator_avatar_url: string;
  cover_asset_url: string;
  preview_assets?: Record<string, unknown>;
  preview_frame_asset_urls?: string[] | null;
  public_url?: string;
  status: string;
  like_count: number;
  changed_since_review?: boolean;
}

export interface GalleryPreviewUpdateInput {
  cover_asset_url?: string;
  preview_frame_asset_urls?: string[];
  preview_assets?: Record<string, unknown>;
  reason?: string;
}

export interface GallerySiteCandidate {
  id: string;
  owner_user_id: string;
  owner_display_name?: string;
  owner_email?: string;
  owner_avatar_url?: string;
  agent_id: string;
  site_name: string;
  source_url: string;
  title: string;
  created_at?: string;
  already_in_book: boolean;
  submission_id?: string;
  submission_status?: string;
}

export interface ListGallerySiteCandidatesResponse {
  candidates: GallerySiteCandidate[];
  has_more?: boolean;
  limit: number;
  offset: number;
}

export interface ListPublicGalleryResponse {
  submissions: GallerySubmission[];
  items?: GallerySubmission[];
  has_more: boolean;
  next_cursor?: string;
}

async function adminGalleryJSON<T>(
  path: string,
  init: RequestInit | undefined,
  errMsg: string
): Promise<T> {
  const response = await authenticatedFetch(path, init);
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, errMsg));
  }
  return response.json();
}

function adminGalleryAction(
  id: string,
  segment: 'hide' | 'restore',
  body: object,
  errMsg: string
): Promise<GallerySubmission> {
  return adminGalleryJSON<{ submission: GallerySubmission }>(
    `/v1/admin/gallery/submissions/${id}/${segment}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    errMsg
  ).then(b => b.submission);
}

export function fetchGallerySiteCandidates(
  limit = 50,
  offset = 0
): Promise<ListGallerySiteCandidatesResponse> {
  return adminGalleryJSON(
    `/v1/admin/gallery/site-candidates?limit=${limit}&offset=${offset}`,
    undefined,
    'Failed to fetch site candidates'
  );
}

export async function fetchGalleryPublicFeed(
  limit = 50,
  cursor?: string
): Promise<ListPublicGalleryResponse> {
  // Public feed does not require auth; we still authenticate so admin runs
  // through the same code path / cookie context.
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) {
    params.set('cursor', cursor);
  }
  const body = await adminGalleryJSON<ListPublicGalleryResponse>(
    `/v1/public/gallery?${params.toString()}`,
    undefined,
    'Failed to fetch public gallery feed'
  );
  return {
    ...body,
    submissions: body.submissions ?? body.items ?? [],
  };
}

export function hideGallerySubmission(
  id: string,
  reason = ''
): Promise<GallerySubmission> {
  return adminGalleryAction(
    id,
    'hide',
    { reason },
    'Failed to hide gallery submission'
  );
}

export function restoreGallerySubmission(
  id: string,
  reason = ''
): Promise<GallerySubmission> {
  return adminGalleryAction(
    id,
    'restore',
    { reason },
    'Failed to restore gallery submission'
  );
}

export function updateGallerySubmissionPreview(
  id: string,
  input: GalleryPreviewUpdateInput
): Promise<GallerySubmission> {
  return adminGalleryJSON<{ submission: GallerySubmission }>(
    `/v1/admin/gallery/submissions/${id}/preview`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
    'Failed to update gallery preview'
  ).then(b => b.submission);
}

export function clearGallerySubmissionPreview(
  id: string,
  reason = ''
): Promise<GallerySubmission> {
  return adminGalleryJSON<{ submission: GallerySubmission }>(
    `/v1/admin/gallery/submissions/${id}/preview`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
    'Failed to clear gallery preview'
  ).then(b => b.submission);
}

export async function uploadGallerySubmissionMedia(
  id: string,
  file: File,
  type: 'cover' | 'video'
): Promise<{
  status: string;
  path: string;
  url: string;
  submission: GallerySubmission;
}> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await authenticatedFetch(
    `/v1/admin/gallery/submissions/${id}/media?type=${type}`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(response, `Failed to upload ${type}`)
    );
  }

  return response.json();
}

export function publishGallerySiteCandidate(
  candidate: Pick<GallerySiteCandidate, 'agent_id' | 'site_name'>
): Promise<GallerySubmission> {
  return adminGalleryJSON<{ submission: GallerySubmission }>(
    `/v1/admin/gallery/site-candidates/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(candidate),
    },
    'Failed to publish site candidate'
  ).then(b => b.submission);
}

export async function deleteGallerySubmission(id: string): Promise<void> {
  const response = await authenticatedFetch(
    `/v1/admin/gallery/submissions/${id}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(response, 'Failed to delete gallery submission')
    );
  }
}

export type JSONRecord = Record<string, unknown>;

export interface LLMProvider {
  id: string;
  key: string;
  display_name: string;
  provider_type: string;
  base_url: string;
  api_key: string;
  auth_header: string;
  mode: string;
  headers: JSONRecord;
  options: JSONRecord;
  enabled: boolean;
  updated_at?: string | null;
}

export interface LLMModel {
  id: string;
  key: string;
  display_name: string;
  provider_id: string;
  upstream_model: string;
  model_namespace: string;
  capabilities: JSONRecord;
  defaults: JSONRecord;
  enabled: boolean;
}

export interface LLMRoute {
  id: string;
  key: string;
  purpose: string;
  model_id: string;
  fallback_model_ids: string[];
  options: JSONRecord;
  enabled: boolean;
}

export interface WillowTemplate {
  id: string;
  template_id: string;
  willow_template_id: string;
  model_id: string;
  provider_id: string;
  name: string;
  model: string;
  provider: string;
  provider_type: string;
  provider_config: JSONRecord;
  request_headers: Record<string, string>;
  image_config: JSONRecord;
  vision_describer_config: JSONRecord;
  analyze_config: JSONRecord;
  max_tokens: number;
  context_tokens: number;
  supports_images?: boolean | null;
  hidden: boolean;
  purpose: string;
  created_at?: number | null;
}

export interface WillowInitialAgent {
  id: string;
  slot: string;
  display_name: string;
  description: string;
  template_id: string;
  is_default: boolean;
  is_router: boolean;
  role: string;
  sort_order: number;
  enabled: boolean;
  avatar_url?: string | null;
  avatar_mime_type?: string | null;
  avatar_size_bytes?: number | null;
}

export interface WillowAgentGroup {
  group_id: string;
  name?: string;
  purpose?: string;
  home_agent_id?: string;
  home_agent_name?: string;
  home_agent_status?: string;
  current_template_id?: string;
  current_template_name?: string;
  operation_eligible: boolean;
  ineligible_reason?: string;
}

export interface ListWillowAgentGroupsResponse {
  groups: WillowAgentGroup[];
  total_count: number;
  limit: number;
  offset: number;
}

export type WillowGroupOperationType = 'update_home_agent_template';

export type WillowGroupOperationJobStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'partial_failed'
  | 'failed'
  | 'canceled';

export type WillowGroupOperationItemStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped_drift'
  | 'canceled';

export type WillowGroupOperationPreviewStatus =
  | 'will_update'
  | 'already_target'
  | 'expected_failure';

export interface WillowGroupOperationInput {
  operation_type: WillowGroupOperationType;
  group_ids: string[];
  params: {
    target_template_id: string;
  };
  reason?: string;
  idempotency_key?: string;
}

export interface WillowGroupOperationPreviewItem {
  group_id: string;
  group_name?: string;
  home_agent_id?: string;
  current_template_id?: string;
  target_template_id: string;
  preview_status: WillowGroupOperationPreviewStatus | string;
  message?: string;
  skipped_reason?: string;
}

export interface WillowGroupOperationPreview {
  operation_type: WillowGroupOperationType;
  target_template_id: string;
  target_template_name?: string;
  total_count: number;
  affected_count: number;
  already_target_count: number;
  expected_failure_count: number;
  items: WillowGroupOperationPreviewItem[];
}

export interface WillowGroupOperationJob {
  id: string;
  operation_type: WillowGroupOperationType | string;
  status: WillowGroupOperationJobStatus | string;
  params: JSONRecord;
  idempotency_key?: string;
  total_count: number;
  succeeded_count: number;
  failed_count: number;
  skipped_drift_count: number;
  canceled_count: number;
  created_by_user_id: string;
  created_by_email: string;
  reason: string;
  cancel_requested_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WillowGroupOperationItem {
  id: string;
  job_id: string;
  group_id: string;
  group_name?: string;
  operation_type: WillowGroupOperationType | string;
  status: WillowGroupOperationItemStatus | string;
  expected_home_agent_id?: string;
  expected_current_template_id?: string;
  target_template_id: string;
  actual_home_agent_id?: string;
  actual_template_id?: string;
  attempt: number;
  last_error?: string;
  skipped_reason?: string;
  retried_by_user_id?: string;
  retried_by_email?: string;
  retried_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListWillowGroupOperationsResponse {
  operations: WillowGroupOperationJob[];
  total: number;
  limit: number;
  offset: number;
}

export interface WillowGroupOperationDetail {
  job: WillowGroupOperationJob;
  items: WillowGroupOperationItem[];
  items_total: number;
  items_limit: number;
  items_offset: number;
}

export interface WillowMainInitialAgentInput {
  display_name: string;
  description: string;
  template_id: string;
}

export interface LLMServiceBinding {
  id: string;
  service_key: string;
  provider_id: string;
  model_id: string;
  route_id: string;
  config: JSONRecord;
  enabled: boolean;
}

export interface LLMConfigStatus {
  source_mode: string;
  revision: number;
  updated_at?: string | null;
  sync_summary: Record<string, number>;
  restart_required: boolean;
}

async function adminJSON<T>(
  path: string,
  options: RequestInit | undefined,
  fallback: string
): Promise<T> {
  const response = await authenticatedFetch(path, options);
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, fallback));
  }
  return response.json();
}

function jsonHeaders(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

function patchJSON(body: unknown): RequestInit {
  return {
    ...jsonHeaders(body),
    method: 'PATCH',
  };
}

export function fetchLLMProviders(): Promise<{ providers: LLMProvider[] }> {
  return adminJSON(
    '/v1/admin/llm/providers',
    undefined,
    'Failed to fetch providers'
  );
}

export function saveLLMProvider(input: Partial<LLMProvider>, id?: string) {
  return adminJSON<{ provider: LLMProvider; restart_required: boolean }>(
    id ? `/v1/admin/llm/providers/${id}` : '/v1/admin/llm/providers',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save provider'
  );
}

export function fetchLLMModels(): Promise<{ models: LLMModel[] }> {
  return adminJSON('/v1/admin/llm/models', undefined, 'Failed to fetch models');
}

export function saveLLMModel(input: Partial<LLMModel>, id?: string) {
  return adminJSON<{ model: LLMModel; restart_required: boolean }>(
    id ? `/v1/admin/llm/models/${id}` : '/v1/admin/llm/models',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save model'
  );
}

export function fetchLLMRoutes(): Promise<{ routes: LLMRoute[] }> {
  return adminJSON('/v1/admin/llm/routes', undefined, 'Failed to fetch routes');
}

export function saveLLMRoute(input: Partial<LLMRoute>, id?: string) {
  return adminJSON<{ route: LLMRoute; restart_required: boolean }>(
    id ? `/v1/admin/llm/routes/${id}` : '/v1/admin/llm/routes',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save route'
  );
}

export function fetchWillowTemplates(): Promise<{
  templates: WillowTemplate[];
}> {
  return adminJSON(
    '/v1/admin/willow/templates',
    undefined,
    'Failed to fetch Willow templates'
  );
}

export function saveWillowTemplate(
  input: Partial<WillowTemplate>,
  id?: string
) {
  return adminJSON<{ template: WillowTemplate; restart_required: boolean }>(
    id ? `/v1/admin/willow/templates/${id}` : '/v1/admin/willow/templates',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save Willow template'
  );
}

export function syncWillowTemplates(): Promise<{
  status: string;
  restart_required: boolean;
}> {
  return adminJSON(
    '/v1/admin/willow/templates/sync',
    { method: 'POST' },
    'Failed to sync Willow templates'
  );
}

export function syncWillowTemplate(
  id: string
): Promise<{ status: string; restart_required: boolean }> {
  return adminJSON(
    `/v1/admin/willow/templates/${id}/sync`,
    { method: 'POST' },
    'Failed to sync Willow template'
  );
}

export function fetchWillowInitialAgents(): Promise<{
  initial_agents: WillowInitialAgent[];
}> {
  return adminJSON(
    '/v1/admin/willow/initial-agents',
    undefined,
    'Failed to fetch initial agents'
  );
}

export function saveWillowInitialAgent(
  input: Partial<WillowInitialAgent>,
  id?: string
) {
  return adminJSON<{
    initial_agent: WillowInitialAgent;
    restart_required: boolean;
  }>(
    id
      ? `/v1/admin/willow/initial-agents/${id}`
      : '/v1/admin/willow/initial-agents',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save initial agent'
  );
}

export function setMainWillowInitialAgent(input: WillowMainInitialAgentInput) {
  return adminJSON<{
    initial_agent: WillowInitialAgent;
    restart_required: boolean;
  }>(
    '/v1/admin/willow/initial-agents/main',
    jsonHeaders(input),
    'Failed to set main initial agent'
  );
}

export async function uploadWillowInitialAgentAvatar(
  id: string,
  file: File
): Promise<{ initial_agent: WillowInitialAgent; restart_required: boolean }> {
  const formData = new FormData();
  formData.append('avatar', file);
  const response = await authenticatedFetch(
    `/v1/admin/willow/initial-agents/${encodeURIComponent(id)}/avatar`,
    {
      method: 'POST',
      body: formData,
    }
  );
  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        'Failed to upload initial agent avatar'
      )
    );
  }
  return response.json();
}

export function clearWillowInitialAgentAvatar(id: string) {
  return adminJSON<{
    initial_agent: WillowInitialAgent;
    restart_required: boolean;
  }>(
    `/v1/admin/willow/initial-agents/${encodeURIComponent(id)}/avatar`,
    { method: 'DELETE' },
    'Failed to clear initial agent avatar'
  );
}

export function fetchWillowAgentGroups(params?: {
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<ListWillowAgentGroupsResponse> {
  const query = new URLSearchParams();
  if (params?.q) query.set('q', params.q);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return adminJSON(
    `/v1/admin/willow/agent-groups${suffix}`,
    undefined,
    'Failed to fetch Willow groups'
  );
}

export function previewWillowGroupOperation(
  input: WillowGroupOperationInput
): Promise<WillowGroupOperationPreview> {
  return adminJSON(
    '/v1/admin/willow/operations:preview',
    jsonHeaders(input),
    'Failed to preview Willow operation'
  );
}

export function createWillowGroupOperation(
  input: WillowGroupOperationInput
): Promise<WillowGroupOperationJob> {
  return adminJSON(
    '/v1/admin/willow/operations',
    jsonHeaders(input),
    'Failed to create Willow operation'
  );
}

export function fetchWillowGroupOperations(params?: {
  limit?: number;
  offset?: number;
}): Promise<ListWillowGroupOperationsResponse> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return adminJSON(
    `/v1/admin/willow/operations${suffix}`,
    undefined,
    'Failed to fetch Willow operations'
  );
}

export function fetchWillowGroupOperation(
  id: string,
  params?: { limit?: number; offset?: number; status?: string }
): Promise<WillowGroupOperationDetail> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  if (params?.status) query.set('status', params.status);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return adminJSON(
    `/v1/admin/willow/operations/${encodeURIComponent(id)}${suffix}`,
    undefined,
    'Failed to fetch Willow operation'
  );
}

export function cancelWillowGroupOperation(
  id: string
): Promise<WillowGroupOperationJob> {
  return adminJSON(
    `/v1/admin/willow/operations/${encodeURIComponent(id)}/cancel`,
    { method: 'POST' },
    'Failed to cancel Willow operation'
  );
}

export function retryWillowGroupOperationItem(
  jobId: string,
  itemId: string
): Promise<WillowGroupOperationItem> {
  return adminJSON(
    `/v1/admin/willow/operations/${encodeURIComponent(jobId)}/items/${encodeURIComponent(itemId)}/retry`,
    { method: 'POST' },
    'Failed to retry Willow operation item'
  );
}

export function fetchLLMServiceBindings(): Promise<{
  service_bindings: LLMServiceBinding[];
}> {
  return adminJSON(
    '/v1/admin/llm/service-bindings',
    undefined,
    'Failed to fetch service bindings'
  );
}

export function saveLLMServiceBinding(
  input: Partial<LLMServiceBinding>,
  id?: string
) {
  return adminJSON<{
    service_binding: LLMServiceBinding;
    restart_required: boolean;
  }>(
    id
      ? `/v1/admin/llm/service-bindings/${id}`
      : '/v1/admin/llm/service-bindings',
    id ? patchJSON(input) : jsonHeaders(input),
    'Failed to save service binding'
  );
}

export function fetchLLMConfigStatus(): Promise<LLMConfigStatus> {
  return adminJSON(
    '/v1/admin/llm/config-status',
    undefined,
    'Failed to fetch config status'
  );
}
